// src/components/header/AppHeader.tsx
// UPDATED: Unified glass header - same bg-white/80 backdrop-blur-xl for top bar and context band
// UPDATED: Normalized heights (72px top, 40px context) and padding (px-6)
// UPDATED: Default upgradeDestination changed to /upgrade

import { APP_HEADER_ROW_PX } from "./headerMetrics";
import HeaderLogo from "./HeaderLogo";
import HeaderSearch from "./HeaderSearch";
import HeaderUpgradeCard from "./HeaderUpgradeCard";
import HeaderUserMenu from "./HeaderUserMenu";
import { PageContextRow } from "./PageContextRow";
import { useScroll } from "../../hooks/useScroll";
import { AskDvaButton } from "../../help/AskDvaButton";

type Plan = "freemium" | "pro";

interface Breadcrumb {
  label: string;
  href?: string;
}

interface AppHeaderProps {
  workspaceTitle?: string;
  plan: Plan;
  onSearch?: (q: string) => void;
  user: { name: string; avatarUrl?: string };
  variant?: "glass" | "gradient";
  headline?: string;
  contextTitle: string;
  breadcrumbs?: Breadcrumb[];
  tabs?: string[];
  activeTab?: "Tools" | "Documents" | "Messages";
  onTabChange?: (tab: "Tools" | "Documents" | "Messages") => void;
  messageCount?: number;
  upgradeDestination?: string;
  rightSlot?: React.ReactNode;
  /** Enterprise mode: hide the storefront search + upgrade chrome (X-1 · FIX 2). */
  enterpriseMode?: boolean;
  /** Slot rendered at the top of the user-menu dropdown (the enterprise mode toggle). */
  userMenuExtra?: React.ReactNode;
  /** Renders the header "Ask DVA" button when provided (EA routes). */
  onHelpClick?: () => void;
  helpOpen?: boolean;
}

export default function AppHeader({
  workspaceTitle = "Data Value Accelerator",
  plan,
  onSearch,
  user,
  variant = "glass",
  headline = "",
  contextTitle,
  breadcrumbs,
  tabs,
  activeTab = "Tools",
  onTabChange,
  messageCount = 0,
  upgradeDestination = "/upgrade",  // CHANGED: from "/freemium/upgrade"
  rightSlot,
  enterpriseMode = false,
  userMenuExtra,
  onHelpClick,
  helpOpen = false,
}: AppHeaderProps) {
  const scrolled = useScroll(20);

  // Unified glass background with dynamic shadows on scroll
  const headerClasses = [
    "sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b transition-all duration-300",
    scrolled
      ? "border-slate-300 shadow-[0_4px_6px_rgba(0,0,0,.1),0_12px_24px_rgba(24,54,94,.15),0_20px_40px_rgba(24,54,94,.1)]"
      : "border-slate-200 shadow-[0_1px_2px_rgba(0,0,0,.04),0_4px_12px_rgba(24,54,94,.06)]",
  ].join(" ");

  return (
    <header className={headerClasses} role="banner">
      {/* Optional gradient accent bar */}
      {variant === "gradient" && (
        <div className="h-1 bg-gradient-to-r from-[#18365E] to-[#FF6E4C]" aria-hidden="true" />
      )}
      
      {/* Main row (72px) - Unified height and padding */}
      <div className="px-6 flex items-center gap-4" style={{ height: APP_HEADER_ROW_PX }}>
        {/* Left: Logo + Title */}
        <div className="flex items-center gap-3 flex-shrink-0">
          <div className="p-2 rounded-lg bg-white/60 shadow-sm">
            <HeaderLogo />
          </div>
          <div className="text-lg font-semibold text-slate-900 whitespace-nowrap">
            {workspaceTitle}
          </div>
        </div>

        {/* Center: Search — hidden in Enterprise mode (the enterprise surface replaces the
            storefront content, so its chrome is suppressed). */}
        {!enterpriseMode && (
          <div className="hidden md:block flex-1 max-w-2xl mx-auto">
            <HeaderSearch onSearch={onSearch} />
          </div>
        )}

        {/* Right: Welcome Message (Pro) / Upgrade (Freemium) + User. Enterprise mode always
            greets (never shows the Upgrade card). */}
        <div className="ml-auto flex items-center gap-3 flex-shrink-0">
          {onHelpClick && <AskDvaButton open={helpOpen} onClick={onHelpClick} />}
          {!enterpriseMode && plan === "freemium" ? (
            <HeaderUpgradeCard destination={upgradeDestination} />
          ) : (
            <span className="hidden md:block text-sm text-slate-700" style={{ fontWeight: 500 }}>
              Welcome back, {user.name}!
            </span>
          )}
          <HeaderUserMenu user={user} extra={userMenuExtra} />
        </div>
      </div>

      {/* Context band - Only show if breadcrumbs exist (non-Pro Journey pages) */}
      {Array.isArray(breadcrumbs) && breadcrumbs.length > 0 && (
        <div className="border-t border-slate-200">
          <PageContextRow
            headline={headline}
            title={contextTitle}
            breadcrumbs={breadcrumbs}
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={onTabChange ? (t: string) => {
              if (t === "Tools" || t === "Documents" || t === "Messages") {
                onTabChange(t);
              }
            } : undefined}
            messageCount={messageCount}
            rightSlot={rightSlot}
          />
        </div>
      )}
    </header>
  );
}